import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const EMOJI_BY_OCCASION: Record<string, string[]> = {
  birthday: ["🎂", "🎈", "🎉", "🎁", "🥳", "🍰", "✨", "🎊"],
  farewell: ["👋", "💼", "🌟", "🍀", "✈️", "💐", "🥂"],
  baby: ["👶", "🍼", "🧸", "🎀", "⭐", "💕", "🌟"],
  wedding: ["💍", "💐", "👰", "🤵", "💕", "🥂", "🌹", "✨"],
  "thank-you": ["🙏", "💐", "💖", "✨", "🌟", "💝"],
  retirement: ["🎉", "🌴", "⛱️", "🥂", "🎊", "✨", "🍾"],
  "get-well": ["🌷", "💐", "🌸", "💖", "🍵", "🌈", "✨"],
  sympathy: ["🕊️", "🤍", "🌷", "🌿", "💐"],
  graduation: ["🎓", "📚", "🎉", "⭐", "🥳", "✨", "🏆"],
  teacher: ["🍎", "📚", "✏️", "⭐", "💐", "🌟"],
  anniversary: ["💕", "💍", "🌹", "🥂", "✨", "💖", "🍾"],
  christmas: ["🎄", "🎅", "❄️", "🎁", "⛄", "✨", "🦌"],
  valentines: ["❤️", "💕", "🌹", "💝", "💘", "✨"],
  congratulations: ["🎉", "🎊", "🥂", "🏆", "⭐", "✨", "🍾"],
};

const DEFAULT_EMOJIS = ["✨", "🎉", "💖", "🌟", "🎊"];

interface Bubble {
  id: number;
  emoji: string;
  left: number;
  duration: number;
  delay: number;
  size: number;
  drift: number;
}

interface FloatingEmojisProps {
  occasion?: string | null;
}

const FloatingEmojis = ({ occasion }: FloatingEmojisProps) => {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);

  useEffect(() => {
    const pool = (occasion && EMOJI_BY_OCCASION[occasion]) || DEFAULT_EMOJIS;
    let nextId = 0;

    const spawn = () => {
      const b: Bubble = {
        id: nextId++,
        emoji: pool[Math.floor(Math.random() * pool.length)],
        left: Math.random() * 100,
        duration: 6 + Math.random() * 5,
        delay: 0,
        size: 22 + Math.random() * 22,
        drift: (Math.random() - 0.5) * 80,
      };
      setBubbles((prev) => [...prev.slice(-24), b]);
      window.setTimeout(() => {
        setBubbles((prev) => prev.filter((x) => x.id !== b.id));
      }, (b.duration + 0.5) * 1000);
    };

    // Initial burst
    for (let i = 0; i < 4; i++) {
      window.setTimeout(spawn, i * 400);
    }
    const interval = window.setInterval(spawn, 900);
    return () => window.clearInterval(interval);
  }, [occasion]);

  return createPortal(
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 overflow-hidden"
      style={{ zIndex: 5 }}
    >
      {bubbles.map((b) => (
        <span
          key={b.id}
          style={{
            position: "absolute",
            left: `${b.left}%`,
            bottom: "-60px",
            fontSize: `${b.size}px`,
            animation: `floatEmojiUp ${b.duration}s ease-in forwards`,
            ["--drift" as string]: `${b.drift}px`,
            willChange: "transform, opacity",
          }}
        >
          {b.emoji}
        </span>
      ))}
      <style>{`
        @keyframes floatEmojiUp {
          0% { transform: translate3d(0, 0, 0) scale(0.6); opacity: 0; }
          15% { opacity: 0.95; transform: translate3d(0, -10vh, 0) scale(1); }
          70% { opacity: 0.9; }
          100% { transform: translate3d(var(--drift), -110vh, 0) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default FloatingEmojis;
